import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroll-component';

import {
  ChatConversationItem,
  useGetTrainerMessagesPage,
} from 'src/api/mutations/useGetTrainerMessages';
import { useGetTrainerClients } from 'src/api/mutations/useGetTrainerClients';
import { useChatRealtime } from 'src/hooks/useChatRealtime';
import { useInquiriesRealtime } from 'src/hooks/useInquiriesRealtime';
import { useCheckinsRealtime } from 'src/hooks/useCheckinsRealtime';
import { auth } from 'src/config/firebase';
import { getSpecialMessageInfo } from './types';
import { InquiriesList } from './components/InquiriesList';
import { CheckinsList } from '../Checkins/CheckinsList';
import { CheckinDetails } from '../Checkins/CheckinDetails';
import { PushDevicesManager } from 'src/components/PushDevicesManager/PushDevicesManager';
import { getUserPushSubscriptions, getCurrentFCMToken } from 'src/services/push/registerPush';
import { sessionStore } from 'src/store/sessionStore';

import { ReactComponent as SearchIcon } from 'src/assets/images/onboarding-trainer/dashboard/clients/search.svg';
import { ReactComponent as FilterIcon } from 'src/assets/images/onboarding-trainer/dashboard/clients/filter.svg';
import { ReactComponent as EmptyMessagesIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/empty-messages.svg';
import { ReactComponent as ClearSearchIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/clear-search.svg';
import { ReactComponent as DropdownChevronIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/dropdown-chevron.svg';
import { ReactComponent as BellEnabledIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/notifications/bell-enabled.svg';
import { ReactComponent as BellDisabledIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/notifications/bell-disabled.svg';
import '../../DashboardScreens.scss';
import './DashboardMessages.scss';

export const DashboardMessages: FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUser = sessionStore((state) => state.currentUser);
  const trainerUid = auth.currentUser?.uid || currentUser?.uid || '';
  const [pushManagerOpen, setPushManagerOpen] = useState(false);
  const [isPushEnabled, setIsPushEnabled] = useState<boolean>(false);
  const [isPushLoading, setIsPushLoading] = useState<boolean>(true);
  const isInitialPushCheckRef = useRef<boolean>(true);

  // Check push subscription state - only show loading indicator initially
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        setIsPushLoading(false);
        isInitialPushCheckRef.current = false;
        setIsPushEnabled(false);
        return;
      }
      if (isInitialPushCheckRef.current) {
        setIsPushLoading(true);
      }
      try {
        const [subs, token] = await Promise.all([
          getUserPushSubscriptions(user.uid, 'trainer'),
          getCurrentFCMToken(),
        ]);
        const hasPermission =
          typeof window !== 'undefined' && 'Notification' in window
            ? Notification.permission === 'granted'
            : false;
        const storedSubId =
          typeof window !== 'undefined' ? localStorage.getItem('minipt_current_fcm_sub_id') : null;
        const isThisDeviceRegistered = subs.some(
          (s) => (token && s.fcm_token === token) || (storedSubId && s.id === storedSubId),
        );
        setIsPushEnabled(isThisDeviceRegistered && hasPermission);
      } catch {
        setIsPushEnabled(false);
      } finally {
        if (isInitialPushCheckRef.current) {
          setIsPushLoading(false);
          isInitialPushCheckRef.current = false;
        }
      }
    });

    return () => unsubscribe();
  }, [pushManagerOpen]);

  // Filters and state
  const tabFromUrl = searchParams.get('tab') as 'messages' | 'inquiries' | 'checkins' | null;
  const [activeTab, setActiveTab] = useState<'messages' | 'inquiries' | 'checkins'>(
    tabFromUrl || 'messages',
  );

  useEffect(() => {
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl, activeTab]);
  const initialCheckinId = searchParams.get('checkinId') || null;
  const [selectedCheckinId, setSelectedCheckinId] = useState<string | null>(initialCheckinId);
  const [sessionReadCheckinIds, setSessionReadCheckinIds] = useState<Set<string>>(
    () => new Set(initialCheckinId ? [initialCheckinId] : []),
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [clientFilter, setClientFilter] = useState('all'); // 'all' or client uid
  const [sortFilter, setSortFilter] = useState('newest'); // 'newest', 'oldest', 'name_asc'
  const [isMobileFilterVisible, setIsMobileFilterVisible] = useState(false);

  // Pagination & Data state
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [allConversations, setAllConversations] = useState<ChatConversationItem[]>([]);
  const [hasMore, setHasMore] = useState(true);

  // Fetch options for the client dropdown
  const { data: clientsData } = useGetTrainerClients('all', '', 'name_asc', 1, 1000);
  const clientOptions = useMemo(() => {
    const list = clientsData?.clients || [];
    return [
      { key: 'all', label: 'All Clients' },
      ...list.map((c) => ({ key: c.uid, label: `${c.name} ${c.surname}` })),
    ];
  }, [clientsData]);

  const clientInfoMap = useMemo(() => {
    const map: Record<string, { fullName: string; profilePicture: string | null }> = {};
    (clientsData?.clients || []).forEach((c) => {
      const fullName = `${c.name || ''} ${c.surname || ''}`.trim() || c.email || 'Client';
      map[c.uid] = {
        fullName,
        profilePicture: c.avatarUrl || null,
      };
    });
    return map;
  }, [clientsData]);

  // Realtime updates
  const { realtimeUpdates } = useChatRealtime();

  const { inquiries, isLoading: isLoadingInquiries } = useInquiriesRealtime(trainerUid);
  const { checkins: realtimeCheckins } = useCheckinsRealtime(trainerUid);

  // Ref to reset pagination when filters change
  const prevFiltersRef = useRef({ clientFilter, sortFilter, debouncedSearch });

  // To avoid querying when we are manually managing state from a new query
  const [isFetchingPage, setIsFetchingPage] = useState(false);

  // The actual query
  const {
    data: pageData,
    isFetching,
    isLoading: isMessagesLoading,
  } = useGetTrainerMessagesPage(
    {
      search: debouncedSearch,
      clientId: clientFilter,
      sort: sortFilter,
      limit: 30,
      cursor,
    },
    !isFetchingPage, // enable if not already manually managing
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Handle filter changes (Reset pagination)
  useEffect(() => {
    const prev = prevFiltersRef.current;
    if (
      prev.clientFilter !== clientFilter ||
      prev.sortFilter !== sortFilter ||
      prev.debouncedSearch !== debouncedSearch
    ) {
      prevFiltersRef.current = { clientFilter, sortFilter, debouncedSearch };
      setAllConversations([]);
      setCursor(undefined);
      setHasMore(true);
      setIsFetchingPage(false);
    }
  }, [clientFilter, sortFilter, debouncedSearch]);

  // Merge newly fetched page data
  useEffect(() => {
    if (pageData && !isFetching) {
      setAllConversations((prev) => {
        const existingIds = new Set(prev.map((c) => c.chatId));
        const newItems = pageData.items.filter((c) => !existingIds.has(c.chatId));
        return [...prev, ...newItems];
      });
      setHasMore(pageData.hasMore);
      setIsFetchingPage(true); // pause automatic query re-runs until we explicitly ask for next page
    }
  }, [pageData, isFetching]);

  const loadNextPage = () => {
    if (pageData?.nextCursor && hasMore) {
      setCursor(pageData.nextCursor);
      setIsFetchingPage(false); // trigger next query
    }
  };

  // Merge Realtime Updates
  // When realtimeUpdates changes, we apply it to our loaded conversations
  const displayConversations = useMemo(() => {
    let merged = [...allConversations];

    // For every updated chat in the realtime listener
    Object.values(realtimeUpdates).forEach((updatedChat) => {
      // If we have a client filter active and this chat isn't for that client, skip
      if (clientFilter !== 'all' && updatedChat.client.uid !== clientFilter) {
        return;
      }

      // Basic text search filter for realtime (if applicable)
      if (debouncedSearch) {
        const term = debouncedSearch.toLowerCase();
        if (!updatedChat.lastMessage.body.toLowerCase().includes(term)) {
          return; // Doesn't match current search
        }
      }

      const idx = merged.findIndex((c) => c.chatId === updatedChat.chatId);
      if (idx !== -1) {
        // Update existing
        merged[idx] = { ...merged[idx], ...updatedChat };
      } else {
        // New conversation arrived, insert it (usually at top if newest, but depends on sort)
        merged.push(updatedChat);
      }
    });

    // Re-sort the merged list to ensure realtime updates push things to the top/bottom properly
    merged.sort((a, b) => {
      if (sortFilter === 'newest') {
        return (
          new Date(b.lastMessage.sendTime).getTime() - new Date(a.lastMessage.sendTime).getTime()
        );
      } else if (sortFilter === 'oldest') {
        return (
          new Date(a.lastMessage.sendTime).getTime() - new Date(b.lastMessage.sendTime).getTime()
        );
      } else if (sortFilter === 'name_asc') {
        return (a.client.fullName || '').localeCompare(b.client.fullName || '');
      }
      return 0;
    });

    // Remove duplicates that might occur during rapid realtime insertion
    const seen = new Set();
    merged = merged.filter((c) => {
      if (seen.has(c.chatId)) return false;
      seen.add(c.chatId);
      return true;
    });

    // Filter out chats for deleted clients (not present in clientInfoMap)
    if (Object.keys(clientInfoMap).length > 0) {
      merged = merged.filter((c) => !!clientInfoMap[c.client.uid]);
    }

    // Enrich client profiles
    merged = merged.map((c) => {
      const info = clientInfoMap[c.client.uid];
      if (info) {
        return {
          ...c,
          client: {
            ...c.client,
            fullName:
              info.fullName && info.fullName !== 'Client'
                ? info.fullName
                : c.client.fullName !== 'Unknown' && c.client.fullName !== 'Client'
                  ? c.client.fullName
                  : info.fullName || 'Client',
            profilePicture: info.profilePicture || c.client.profilePicture,
          },
        };
      }
      return c;
    });

    // Remove support conversations from normal messages tab
    merged = merged.filter(
      (c) =>
        !c.isSupport &&
        !c.chatId?.startsWith('support_') &&
        c.client?.fullName !== 'miniPT Support',
    );

    return merged;
  }, [allConversations, realtimeUpdates, clientFilter, sortFilter, debouncedSearch, clientInfoMap]);

  // Computed unread / pending counts
  const unreadMessagesCount = useMemo(() => {
    return displayConversations.filter((c) => {
      const isSentByTrainer =
        c.lastMessage?.senderUid === trainerUid ||
        (c.lastMessage as any)?.sender_uid === trainerUid;
      return c.isUnread && !isSentByTrainer;
    }).length;
  }, [displayConversations, trainerUid]);

  const unreadInquiriesCount = useMemo(() => {
    return inquiries.filter((inq) => {
      const isSentByTrainer =
        inq.message?.sender_role === 'trainer' ||
        inq.message?.sender_uid === trainerUid ||
        inq.message?.send_uid === trainerUid;
      return !isSentByTrainer && !inq.message.is_read;
    }).length;
  }, [inquiries, trainerUid]);

  const unreadCheckinsCount = useMemo(() => {
    return realtimeCheckins.filter(
      (c) =>
        !c.is_read && !sessionReadCheckinIds.has(c.id) && (c.isAppoint || Boolean(c.appointUId)),
    ).length;
  }, [realtimeCheckins, sessionReadCheckinIds]);

  const getClientLabel = () =>
    clientOptions.find((i) => i.key === clientFilter)?.label || 'All Clients';

  const sortItems = [
    { key: 'newest', label: 'Date (Newest)' },
    { key: 'oldest', label: 'Date (Oldest)' },
    { key: 'name_asc', label: 'Name (A-Z)' },
  ];
  const getSortLabel = () => sortItems.find((i) => i.key === sortFilter)?.label || 'Date (Newest)';

  const formatMessageTime = (isoString?: string) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '';
    const now = new Date();
    const isToday =
      date.getDate() === now.getDate() &&
      date.getMonth() === now.getMonth() &&
      date.getFullYear() === now.getFullYear();

    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    const isYesterday =
      date.getDate() === yesterday.getDate() &&
      date.getMonth() === yesterday.getMonth() &&
      date.getFullYear() === yesterday.getFullYear();

    const timeStr = date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    if (isToday) {
      return `Sent at ${timeStr}`;
    }
    if (isYesterday) {
      return 'Yesterday';
    }

    return date.toLocaleDateString([], {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  };

  const getInitials = (fullName: string) => {
    if (!fullName) return 'U';
    const parts = fullName.trim().split(' ');
    if (parts.length > 1) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return fullName.substring(0, 2).toUpperCase();
  };

  const handleTabChange = (tab: 'messages' | 'inquiries' | 'checkins') => {
    setActiveTab(tab);
    setSelectedCheckinId(null);
    setSearchParams({ tab });
  };

  const handleSelectCheckin = (id: string | null) => {
    setSelectedCheckinId(id);
    if (id) {
      setSessionReadCheckinIds((prev) => new Set(prev).add(id));
      setSearchParams({ tab: 'checkins', checkinId: id });
    } else {
      setSearchParams({ tab: 'checkins' });
    }
  };

  const getSearchPlaceholder = () => {
    if (activeTab === 'inquiries') return 'Search inquiries';
    if (activeTab === 'checkins') return 'Search check-ins';
    return 'Search messages';
  };

  const filteredInquiries = useMemo(() => {
    return inquiries.filter((inq) => {
      const isSupport =
        Boolean(inq.message.is_support) ||
        inq.chatId.startsWith('support_') ||
        inq.message.sender_name === 'miniPT Support';
      const clientUid = inq.chatId.split('_').find((id) => id !== currentUser?.uid);
      const clientFromConvo = displayConversations.find((c) => c.chatId === inq.chatId)?.client;
      const clientFromMap = clientUid ? clientInfoMap[clientUid] : undefined;
      const clientFullName = isSupport
        ? 'miniPT Support'
        : clientFromConvo?.fullName || clientFromMap?.fullName || inq.message.sender_name || '';

      if (clientFilter && clientFilter !== 'all') {
        if (isSupport) return false;
        if (clientUid !== clientFilter) return false;
      }
      if (debouncedSearch) {
        const q = debouncedSearch.toLowerCase();
        const nameMatch = clientFullName.toLowerCase().includes(q);
        const bodyMatch =
          (inq.message.body || '').toLowerCase().includes(q) ||
          (inq.message.inquiry_text || '').toLowerCase().includes(q);
        const subjectMatch = (inq.message.inquirySubject || inq.message.subject || '')
          .toLowerCase()
          .includes(q);
        const detailMatch = (inq.message.inquiryDetail || '').toLowerCase().includes(q);
        const typeMatch = (
          inq.message.inquiryType ||
          inq.message.topic ||
          inq.message.category ||
          inq.message.inquiry_category ||
          ''
        )
          .toLowerCase()
          .includes(q);
        if (!nameMatch && !bodyMatch && !subjectMatch && !detailMatch && !typeMatch) return false;
      }
      return true;
    });
  }, [
    inquiries,
    displayConversations,
    clientInfoMap,
    clientFilter,
    debouncedSearch,
    currentUser?.uid,
  ]);

  // If a checkin is selected in Check-ins tab, render the full Details view
  if (activeTab === 'checkins' && selectedCheckinId) {
    return (
      <div
        className="dashboard-screen messages-screen"
        style={{ padding: 0, overflowY: 'hidden', height: '100%', flex: 1, minHeight: 0 }}
      >
        <CheckinDetails checkinId={selectedCheckinId} onBack={() => handleSelectCheckin(null)} />
      </div>
    );
  }

  return (
    <div className="dashboard-screen messages-screen">
      <div className="dashboard-top-section desktop-only">
        <div className="dashboard-header">
          <h1>{activeTab === 'checkins' ? 'Check-ins' : 'Messages'}</h1>
          <button
            onClick={() => setPushManagerOpen(true)}
            className={`push-settings-btn ${isPushLoading ? 'loading' : isPushEnabled ? 'enabled' : 'disabled'}`}
            title={
              isPushLoading
                ? 'Loading notification settings...'
                : isPushEnabled
                  ? 'Notification settings: Active'
                  : 'Notification settings: Disabled'
            }
          >
            {isPushLoading ? (
              <LoadingOutlined style={{ fontSize: 16, color: '#FFA100' }} spin />
            ) : isPushEnabled ? (
              <BellEnabledIcon />
            ) : (
              <BellDisabledIcon />
            )}
          </button>
        </div>

        {/* Filter Bar placed on TOP of tabs */}
        <div className="dashboard-filter-bar">
          <div className="filters-left">
            <div className="search-input">
              <SearchIcon />
              <input
                type="text"
                placeholder={getSearchPlaceholder()}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onBlur={(e) => setSearchQuery(e.target.value.trim())}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="clear-search-btn"
                  aria-label="Clear search"
                >
                  <ClearSearchIcon />
                </button>
              )}
            </div>
          </div>

          <div className="filters-right">
            <div className="filter-label">
              <FilterIcon />
              <span>Filters:</span>
            </div>
            <Dropdown
              menu={{ items: clientOptions, onClick: (e) => setClientFilter(e.key) }}
              trigger={['click']}
              rootClassName="dark-theme-dropdown"
            >
              <div className="filter-item filter-show" style={{ cursor: 'pointer' }}>
                <span>
                  Client: <span className="value">{getClientLabel()}</span>
                </span>
                <DropdownChevronIcon />
              </div>
            </Dropdown>
            <Dropdown
              menu={{ items: sortItems, onClick: (e) => setSortFilter(e.key) }}
              trigger={['click']}
              rootClassName="dark-theme-dropdown"
            >
              <div className="filter-item filter-sort" style={{ cursor: 'pointer' }}>
                <span>
                  Sort: <span className="value">{getSortLabel()}</span>
                </span>
                <DropdownChevronIcon />
              </div>
            </Dropdown>
          </div>
        </div>
      </div>

      <div className="mobile-only">
        <div className="dashboard-top-section">
          <div className="dashboard-header">
            <h1>{activeTab === 'checkins' ? 'Check-ins' : 'Messages'}</h1>
            <button
              onClick={() => setPushManagerOpen(true)}
              className={`push-settings-btn ${isPushLoading ? 'loading' : isPushEnabled ? 'enabled' : 'disabled'}`}
              title={
                isPushLoading
                  ? 'Loading notification settings...'
                  : isPushEnabled
                    ? 'Notification settings: Active'
                    : 'Notification settings: Disabled'
              }
              aria-label="Notification settings"
            >
              {isPushLoading ? (
                <LoadingOutlined style={{ fontSize: 16, color: '#FFA100' }} spin />
              ) : isPushEnabled ? (
                <BellEnabledIcon />
              ) : (
                <BellDisabledIcon />
              )}
            </button>
          </div>

          {/* Filter Bar placed on TOP of tabs for Mobile */}
          <div className="dashboard-filter-bar">
            <div className={`search-filter-row ${isMobileFilterVisible ? 'active' : ''}`}>
              <div className="search-input">
                <SearchIcon />
                <input
                  type="text"
                  placeholder={getSearchPlaceholder()}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onBlur={(e) => setSearchQuery(e.target.value.trim())}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="clear-search-btn"
                    aria-label="Clear search"
                  >
                    <ClearSearchIcon />
                  </button>
                )}
              </div>
              <button
                className={`mobile-filter-btn ${isMobileFilterVisible ? 'active' : ''}`}
                onClick={() => setIsMobileFilterVisible(!isMobileFilterVisible)}
                aria-label="Toggle filters"
              >
                <FilterIcon />
              </button>
            </div>

            {isMobileFilterVisible && (
              <div className="filter-row">
                <Dropdown
                  menu={{ items: clientOptions, onClick: (e) => setClientFilter(e.key) }}
                  trigger={['click']}
                  rootClassName="dark-theme-dropdown"
                >
                  <div className="filter-item filter-show" style={{ cursor: 'pointer' }}>
                    <span>
                      Client: <span className="value">{getClientLabel()}</span>
                    </span>
                    <DropdownChevronIcon />
                  </div>
                </Dropdown>
                <Dropdown
                  menu={{ items: sortItems, onClick: (e) => setSortFilter(e.key) }}
                  trigger={['click']}
                  rootClassName="dark-theme-dropdown"
                >
                  <div className="filter-item filter-sort" style={{ cursor: 'pointer' }}>
                    <span>
                      Sort: <span className="value">{getSortLabel()}</span>
                    </span>
                    <DropdownChevronIcon />
                  </div>
                </Dropdown>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs Navigation moved to body */}
      <div className="tabs-navigation-wrapper">
        <div className="tabs-navigation">
          <button
            className={`tab-btn ${activeTab === 'messages' ? 'active' : ''}`}
            onClick={() => handleTabChange('messages')}
          >
            <span>Messages</span>
            {unreadMessagesCount > 0 && (
              <span className="tab-unread-badge">
                <span className="tab-unread-dot" />
                <span className="count">{unreadMessagesCount}</span>
              </span>
            )}
          </button>
          <button
            className={`tab-btn ${activeTab === 'inquiries' ? 'active' : ''}`}
            onClick={() => handleTabChange('inquiries')}
          >
            <span>Inquiries</span>
            {unreadInquiriesCount > 0 && (
              <span className="tab-unread-badge inquiries">
                <span className="tab-unread-dot inquiries" />
                <span className="count">{unreadInquiriesCount}</span>
              </span>
            )}
          </button>
          <button
            className={`tab-btn ${activeTab === 'checkins' ? 'active' : ''}`}
            onClick={() => handleTabChange('checkins')}
          >
            <span>Check-ins</span>
            {unreadCheckinsCount > 0 && (
              <span className="tab-unread-badge checkins">
                <span className="tab-unread-dot checkins" />
                <span className="count">{unreadCheckinsCount}</span>
              </span>
            )}
          </button>
        </div>
      </div>

      {activeTab === 'messages' && (
        <>
          {(isFetching || isMessagesLoading || pageData === undefined) &&
          allConversations.length === 0 ? (
            <div
              className="loading-container"
              style={{
                padding: '40px 0',
                minHeight: 'auto',
                textAlign: 'center',
                width: '100%',
                color: '#A0A0A0',
              }}
            >
              <Spin
                indicator={
                  <LoadingOutlined className="loading-icon" spin style={{ color: '#F59E0B' }} />
                }
              />
              <div style={{ marginTop: '8px' }}>Loading conversations...</div>
            </div>
          ) : displayConversations.length > 0 ? (
            <div className="messages-list-container">
              <InfiniteScroll
                dataLength={displayConversations.length}
                next={loadNextPage}
                hasMore={hasMore}
                scrollableTarget="scrollableDashboardMain"
                loader={
                  <div
                    className="loading-container"
                    style={{
                      padding: '20px 0',
                      minHeight: 'auto',
                      textAlign: 'center',
                      width: '100%',
                      color: '#A0A0A0',
                    }}
                  >
                    <Spin
                      indicator={
                        <LoadingOutlined
                          className="loading-icon"
                          spin
                          style={{ color: '#F59E0B' }}
                        />
                      }
                    />
                  </div>
                }
              >
                {displayConversations.map((convo) => (
                  <div
                    key={convo.chatId}
                    className="message-item"
                    onClick={() =>
                      navigate(`/onboarding-trainer/dashboard/messages/${convo.chatId}`)
                    }
                  >
                    <div
                      className={`message-avatar ${convo.client.profilePicture ? 'has-image' : ''}`}
                      style={
                        convo.client.profilePicture
                          ? { backgroundImage: `url(${convo.client.profilePicture})` }
                          : undefined
                      }
                    >
                      {!convo.client.profilePicture && getInitials(convo.client.fullName)}
                    </div>
                    <div className="message-content">
                      <div className="message-header">
                        <div className="name-and-time">
                          <h3>{convo.client.fullName}</h3>
                          <span className="message-time">
                            {formatMessageTime(convo.lastMessage.sendTime)}
                          </span>
                        </div>
                        {convo.isUnread &&
                          convo.lastMessage?.senderUid !== trainerUid &&
                          (convo.lastMessage as any)?.sender_uid !== trainerUid && (
                            <div className="unread-indicator">
                              <div className="unread-dot"></div>
                              <span>New</span>
                            </div>
                          )}
                      </div>
                      {(() => {
                        const { specialTag } = getSpecialMessageInfo(convo.lastMessage);
                        if (specialTag) {
                          return (
                            <div className="message-special-tag-row">
                              <span className="special-tag-text">{specialTag}</span>
                            </div>
                          );
                        }
                        return null;
                      })()}
                      <div className="message-preview-row">
                        <p
                          className={
                            convo.isUnread &&
                            convo.lastMessage?.senderUid !== trainerUid &&
                            (convo.lastMessage as any)?.sender_uid !== trainerUid
                              ? 'unread-text'
                              : ''
                          }
                        >
                          {getSpecialMessageInfo(convo.lastMessage).previewText}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </InfiniteScroll>
            </div>
          ) : (
            <div className="dashboard-empty-state">
              <div className="empty-icon">
                <EmptyMessagesIcon />
              </div>
              <h2>
                {debouncedSearch
                  ? `No conversations found for "${debouncedSearch}"`
                  : 'No conversations found'}
              </h2>
              <p>
                {debouncedSearch
                  ? 'Try a different search term or client filter.'
                  : 'You have no conversations yet.'}
              </p>
            </div>
          )}
        </>
      )}

      {activeTab === 'inquiries' && (
        <InquiriesList
          inquiries={filteredInquiries}
          isLoading={isLoadingInquiries}
          conversations={displayConversations}
        />
      )}

      {activeTab === 'checkins' && (
        <CheckinsList
          onSelectCheckin={handleSelectCheckin}
          searchQuery={searchQuery}
          clientFilter={clientFilter}
          sortFilter={sortFilter}
          activeCheckinId={selectedCheckinId || undefined}
        />
      )}

      {/* Push Notification Devices & Settings Modal */}
      <PushDevicesManager open={pushManagerOpen} onClose={() => setPushManagerOpen(false)} />
    </div>
  );
};
